import { aiDraft, createTicket, apiConfigured } from '../api.js'
import {
  clearFieldErrors,
  escapeHtml,
  missingFieldsMessage,
  validateRequiredFields,
} from '../ui.js'

export function renderAi() {
  return `
    <div class="portal-card">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;flex-wrap:wrap;margin-bottom:12px;">
        <div>
          <h1 style="font-size:18px;margin:0 0 4px;">Asistente IA</h1>
          <p class="portal-muted" style="margin:0;">Describe el problema; generaremos un borrador de ticket.</p>
        </div>
        <a href="/" data-link class="portal-btn">Volver</a>
      </div>
      <form id="ai-form" novalidate>
        <div class="portal-field">
          <label class="portal-label" for="company">Empresa *</label>
          <input class="portal-input" id="company" name="company" required autocomplete="organization" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="requester_name">Tu nombre *</label>
          <input class="portal-input" id="requester_name" name="requester_name" required autocomplete="name" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="requester_email">Tu email *</label>
          <input class="portal-input" type="email" id="requester_email" name="requester_email" required autocomplete="email" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="message">¿Qué ocurre? *</label>
          <textarea class="portal-textarea" id="message" name="message" required maxlength="10000"></textarea>
        </div>
        <div id="wifi-box" style="display:none;" class="portal-card"></div>
        <button type="submit" class="portal-btn portal-btn-primary portal-btn-block">Preparar borrador</button>
      </form>
      <div id="ai-draft" style="display:none;margin-top:14px;"></div>
      <p id="ai-msg" class="portal-muted portal-form-msg" role="status"></p>
    </div>
  `
}

export function bindAi(root, { navigate }) {
  const form = root.querySelector('#ai-form')
  const msg = root.querySelector('#ai-msg')
  const draftEl = root.querySelector('#ai-draft')
  const wifiBox = root.querySelector('#wifi-box')
  let lastDraft = null

  form?.addEventListener('input', (ev) => {
    const t = ev.target
    if (t && t.closest) {
      clearFieldErrors(t.closest('.portal-field') || form)
      msg.classList.remove('is-error')
    }
  })

  form?.addEventListener('submit', async (ev) => {
    ev.preventDefault()
    msg.classList.remove('is-error')
    if (!apiConfigured()) {
      msg.textContent = 'Servicio no disponible. Contacta por teléfono o email (pie de página).'
      msg.classList.add('is-error')
      return
    }

    const baseFields = [
      { name: 'company', label: 'Empresa' },
      { name: 'requester_name', label: 'Tu nombre' },
      {
        name: 'requester_email',
        label: 'Tu email',
        test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
      },
      { name: 'message', label: '¿Qué ocurre?' },
    ]

    const wifiVisible = wifiBox.style.display !== 'none' && wifiBox.innerHTML.trim() !== ''
    if (wifiVisible) {
      baseFields.push(
        { name: 'wifi_scope', label: 'Ámbito WiFi' },
        {
          name: 'wifi_location',
          label: 'Ubicación WiFi',
          test: (v, f) => {
            const scope = String(f.elements.namedItem('wifi_scope')?.value || '')
            if (scope === 'whole') return true
            return v.length > 0
          },
        },
        { name: 'wifi_has_internet', label: '¿Hay internet ahora?' },
      )
    }

    const { ok, missing } = validateRequiredFields(form, baseFields)
    if (!ok) {
      msg.textContent = missingFieldsMessage(missing)
      msg.classList.add('is-error')
      return
    }

    const fd = new FormData(form)
    msg.textContent = 'Preparando borrador…'
    draftEl.style.display = 'none'
    try {
      const payload = {
        company: String(fd.get('company') || ''),
        requester_name: String(fd.get('requester_name') || '') || undefined,
        message: String(fd.get('message') || ''),
        wifi_scope: fd.get('wifi_scope') || undefined,
        wifi_location: fd.get('wifi_location') || undefined,
        wifi_has_internet: fd.get('wifi_has_internet') || undefined,
      }
      const data = await aiDraft(payload)
      if (data.notice) {
        msg.textContent = data.notice
      } else {
        msg.textContent = ''
      }
      if (data.needs_wifi_details) {
        wifiBox.style.display = 'block'
        const suggestions = (data.location_suggestions || []).slice(0, 8)
          .map((s) => `<option value="${escapeHtml(s)}">`)
          .join('')
        wifiBox.innerHTML = `
          <h2 style="font-size:14px;margin:0 0 8px;">${escapeHtml(data.wifi_prompt?.title || 'Detalles WiFi')}</h2>
          <p class="portal-muted">${escapeHtml(data.wifi_prompt?.intro || 'Completa estos datos y vuelve a pulsar «Preparar borrador».')}</p>
          <div class="portal-field">
            <label class="portal-label">Ámbito WiFi *</label>
            <select class="portal-select" name="wifi_scope" form="ai-form">
              <option value="">Selecciona…</option>
              <option value="room">Habitación / puesto</option>
              <option value="area">Zona / planta</option>
              <option value="whole">Todo el edificio</option>
            </select>
          </div>
          <div class="portal-field">
            <label class="portal-label">Ubicación WiFi *</label>
            <input class="portal-input" name="wifi_location" form="ai-form" list="wifi-locs" placeholder="Ej. habitación 204, planta 2…" />
            <datalist id="wifi-locs">${suggestions}</datalist>
          </div>
          <div class="portal-field">
            <label class="portal-label">¿Hay internet ahora? *</label>
            <select class="portal-select" name="wifi_has_internet" form="ai-form">
              <option value="">Selecciona…</option>
              <option value="no">No</option>
              <option value="partial">Parcial / inestable</option>
              <option value="wired_only">Solo cable</option>
              <option value="yes">Sí</option>
              <option value="unknown">No lo sé</option>
            </select>
          </div>
          <div class="portal-banner warn" style="margin-top:8px;">Faltan detalles WiFi. Rellena los campos marcados y pulsa otra vez «Preparar borrador».</div>
        `
        msg.textContent = 'Faltan detalles WiFi: ámbito, ubicación (si aplica) e internet.'
        msg.classList.add('is-error')
        wifiBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        return
      }
      wifiBox.style.display = 'none'
      lastDraft = data.draft
      const d = data.draft || {}
      draftEl.style.display = 'block'
      draftEl.innerHTML = `
        <div class="portal-card">
          <h2 style="font-size:15px;">Borrador</h2>
          ${data.notice ? `<div class="portal-banner warn">${escapeHtml(data.notice)}</div>` : ''}
          <div class="portal-field">
            <label class="portal-label">Asunto</label>
            <input class="portal-input" id="draft-subject" value="${escapeHtml(d.subject || '')}" />
          </div>
          <div class="portal-field">
            <label class="portal-label">Descripción</label>
            <textarea class="portal-textarea" id="draft-description">${escapeHtml(d.description || '')}</textarea>
          </div>
          <div class="portal-field">
            <label class="portal-label">Prioridad</label>
            <select class="portal-select" id="draft-priority">
              ${['low','medium','high','urgent'].map((p) =>
                `<option value="${p}" ${p === (d.priority || 'medium') ? 'selected' : ''}>${p}</option>`
              ).join('')}
            </select>
          </div>
          <button type="button" class="portal-btn portal-btn-primary" id="draft-send">Enviar ticket</button>
        </div>
      `
      draftEl.querySelector('#draft-send')?.addEventListener('click', async () => {
        msg.classList.remove('is-error')
        const { ok: sendOk, missing: sendMissing } = validateRequiredFields(form, [
          { name: 'company', label: 'Empresa' },
          { name: 'requester_name', label: 'Tu nombre' },
          {
            name: 'requester_email',
            label: 'Tu email',
            test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
          },
        ])
        if (!sendOk) {
          msg.textContent = missingFieldsMessage(sendMissing)
          msg.classList.add('is-error')
          return
        }
        const email = String(fd.get('requester_email') || '').trim()
        const name = String(fd.get('requester_name') || '').trim()
        msg.textContent = 'Creando ticket…'
        try {
          const created = await createTicket({
            company: String(fd.get('company') || ''),
            requester_name: name,
            requester_email: email,
            subject: draftEl.querySelector('#draft-subject')?.value || d.subject,
            description: draftEl.querySelector('#draft-description')?.value || d.description,
            priority: draftEl.querySelector('#draft-priority')?.value || d.priority || 'medium',
            category: d.category,
            entry_mode: 'ai',
          })
          navigate(created.ticket?.portal_path || `/t/${created.ticket?.access_token}`)
        } catch (e) {
          msg.textContent = e.message || 'Error al crear'
          msg.classList.add('is-error')
        }
      })
    } catch (e) {
      msg.textContent = e.message || 'Error'
      msg.classList.add('is-error')
    }
  })
}
